import type { TaxFlow } from "./tax-flow";

export const vehicleStatuses = [
  "draft",
  "purchased",
  "in_transit",
  "received",
  "in_preparation",
  "available",
  "reserved",
  "sold",
  "delivered",
  "returned",
  "written_off",
] as const;

export type VehicleStatus = (typeof vehicleStatuses)[number];

export const statusLabels: Record<VehicleStatus, string> = {
  draft: "Borrador",
  purchased: "Comprado",
  in_transit: "En tránsito",
  received: "Recepcionado",
  in_preparation: "En preparación",
  available: "Disponible",
  reserved: "Reservado",
  sold: "Vendido",
  delivered: "Entregado",
  returned: "Devuelto",
  written_off: "Baja",
};

export const writeOffReasons = ["scrapping", "export", "total_loss", "discarded"] as const;
export type WriteOffReason = (typeof writeOffReasons)[number];

export const writeOffReasonLabels: Record<WriteOffReason, string> = {
  scrapping: "Desguace",
  export: "Exportación",
  total_loss: "Siniestro total",
  discarded: "Ficha descartada",
};

export const publicationRequirementKeys = [
  "tax_flow",
  "identity",
  "make",
  "model",
  "mileage",
  "target_price",
  "photos",
] as const;

export type PublicationRequirementKey = (typeof publicationRequirementKeys)[number];

export interface PublicationReadinessInput {
  taxFlow?: TaxFlow | null;
  registration?: string | null;
  vin?: string | null;
  make?: string | null;
  model?: string | null;
  mileage?: number | null;
  targetPriceCents?: number | null;
  photoCount: number;
  minPublishPhotos: number;
}

export interface PublicationRequirement {
  key: PublicationRequirementKey;
  label: string;
  complete: boolean;
  detail: string;
  missingLabel: string;
}

export interface PublicationReadiness {
  unlocked: boolean;
  missing: string[];
  requirements: PublicationRequirement[];
}

/**
 * Única lista de requisitos para poner un vehículo a la venta. La API la
 * devuelve a la interfaz y validateTransition consume la misma función: así
 * la guía nunca promete que se puede publicar algo que el dominio rechazará.
 */
export function evaluatePublicationReadiness(input: PublicationReadinessInput): PublicationReadiness {
  const photoGap = Math.max(0, input.minPublishPhotos - input.photoCount);
  const requirements: PublicationRequirement[] = [
    {
      key: "tax_flow",
      label: "Compra y fiscalidad",
      complete: input.taxFlow !== null && input.taxFlow !== undefined,
      detail: input.taxFlow !== null && input.taxFlow !== undefined
        ? "Origen fiscal determinado"
        : "Registra la entrada para determinarla",
      missingLabel: "compra y fiscalidad",
    },
    {
      key: "identity",
      label: "Matrícula o VIN",
      complete: Boolean(input.registration || input.vin),
      detail: input.registration || input.vin ? "Identificación añadida" : "Añade al menos uno",
      missingLabel: "matrícula o VIN",
    },
    {
      key: "make",
      label: "Marca",
      complete: Boolean(input.make),
      detail: input.make ? "Añadida" : "Pendiente",
      missingLabel: "marca",
    },
    {
      key: "model",
      label: "Modelo",
      complete: Boolean(input.model),
      detail: input.model ? "Añadido" : "Pendiente",
      missingLabel: "modelo",
    },
    {
      key: "mileage",
      label: "Kilómetros",
      complete: input.mileage !== null && input.mileage !== undefined,
      detail: input.mileage !== null && input.mileage !== undefined ? "Añadidos" : "Pendientes",
      missingLabel: "kilómetros",
    },
    {
      key: "target_price",
      label: "Precio de venta",
      complete: input.targetPriceCents !== null && input.targetPriceCents !== undefined,
      detail: input.targetPriceCents !== null && input.targetPriceCents !== undefined ? "Definido" : "Pendiente",
      missingLabel: "precio de venta objetivo",
    },
    {
      key: "photos",
      label: "Fotos",
      complete: photoGap === 0,
      detail: photoGap === 0
        ? `${input.photoCount} ${input.photoCount === 1 ? "foto lista" : "fotos listas"}`
        : `${input.photoCount} de ${input.minPublishPhotos}; faltan ${photoGap}`,
      missingLabel: `${photoGap} fotos más`,
    },
  ];
  const missing = requirements.filter((requirement) => !requirement.complete).map((requirement) => requirement.missingLabel);
  return { unlocked: missing.length === 0, missing, requirements };
}

/** Estados en los que el vehículo ya no está en el patrimonio del tenant. */
const soldStatuses: readonly VehicleStatus[] = ["sold", "delivered"];

/** Motivos que describen algo que le pasa a un vehículo físico ya en stock. */
const physicalWriteOffReasons: readonly WriteOffReason[] = ["scrapping", "export", "total_loss"];

/** Estados que exigen flujo fiscal determinado. Reforzado con CHECK en D1. */
const fiscalRequiredStatuses: readonly VehicleStatus[] = [
  "available", "reserved", "sold", "delivered", "returned",
];

export const allowedTransitions: Record<VehicleStatus, readonly VehicleStatus[]> = {
  draft: ["purchased", "written_off"],
  purchased: ["in_transit", "received", "written_off"],
  in_transit: ["received", "written_off"],
  received: ["in_preparation", "written_off"],
  in_preparation: ["available", "written_off"],
  available: ["reserved", "sold", "in_preparation", "written_off"],
  reserved: ["sold", "available"],
  sold: ["delivered", "returned"],
  delivered: ["returned"],
  returned: ["available", "written_off"],
  written_off: [],
};

export interface TransitionSnapshot extends PublicationReadinessInput {
  status: VehicleStatus;
  writeOffReason: WriteOffReason | null;
  taxFlow: TaxFlow | null;
  registration: string | null;
  vin: string | null;
  make: string | null;
  model: string | null;
  mileage: number | null;
  targetPriceCents: number | null;
}

export type TransitionErrorCode =
  | "TRANSICION_NO_PERMITIDA"
  | "REGIMEN_FISCAL_REQUERIDO"
  | "PUBLICACION_INCOMPLETA"
  | "MOTIVO_BAJA_REQUERIDO";

export interface TransitionValidation {
  valid: boolean;
  atypical: boolean;
  missing: readonly string[];
  code: TransitionErrorCode | null;
}

const ok: TransitionValidation = { valid: true, atypical: false, missing: [], code: null };

/**
 * La restricción de 'discarded' al estado 'draft' no es expresable en un CHECK
 * de SQLite, porque la fila no conserva el estado de partida. Se impone aquí.
 */
export function validateTransition(
  vehicle: TransitionSnapshot,
  toStatus: VehicleStatus,
  writeOffReason: WriteOffReason | null,
): TransitionValidation {
  if (!allowedTransitions[vehicle.status].includes(toStatus)) {
    return { valid: false, atypical: true, missing: [], code: "TRANSICION_NO_PERMITIDA" };
  }

  if (toStatus === "written_off") {
    // La regla tiene dos direcciones y las dos importan. Desde `draft` el único
    // motivo válido es `discarded`: un borrador no se ha adquirido, así que no
    // puede ir al desguace ni sufrir un siniestro total. Desde cualquier otro
    // estado el motivo tiene que ser físico, porque `discarded` solo da salida a
    // una ficha que nunca llegó a comprarse.
    const validReason = writeOffReason !== null
      && !soldStatuses.includes(vehicle.status)
      && (vehicle.status === "draft"
        ? writeOffReason === "discarded"
        : physicalWriteOffReasons.includes(writeOffReason));
    if (!validReason) {
      return { valid: false, atypical: false, missing: [], code: "MOTIVO_BAJA_REQUERIDO" };
    }
    return ok;
  }

  if (fiscalRequiredStatuses.includes(toStatus) && vehicle.taxFlow === null) {
    return { valid: false, atypical: false, missing: [], code: "REGIMEN_FISCAL_REQUERIDO" };
  }

  if (toStatus === "available") {
    const { missing } = evaluatePublicationReadiness(vehicle);
    if (missing.length > 0) {
      return { valid: false, atypical: false, missing, code: "PUBLICACION_INCOMPLETA" };
    }
  }

  return ok;
}
